import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { useAuth } from '../lib/auth';
import { openPlanPanel } from '../lib/signup';
import type { Filters } from '../lib/filters';

export type ListMode = 'results' | 'watch';

interface Props {
  filters: Filters;
  listOpen: boolean;
  listMode: ListMode;
  /** Abre la lista en el modo pedido o, si ya estaba en ese modo, la cierra. */
  onToggleList: (mode: ListMode) => void;
}

/** Filas por exportación (el servidor además recorta al cupo mensual del plan). */
const EXPORT_ROWS = 1000;

/**
 * Acciones sobre los resultados, dentro de la barra de filtros: abrir la
 * lista, mis observados, copiar el enlace de la búsqueda y exportar a CSV.
 * Antes vivían en la cabecera de la lista, fija a 116 px del borde: cuando
 * la barra de filtros crecía a tres renglones la tapaba y los botones
 * quedaban inalcanzables.
 */
export function ResultsToolbar({ filters, listOpen, listMode, onToggleList }: Props) {
  const { user, usage, watchItems, refreshUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard API no disponible (http inseguro / permisos) — no rompemos.
    }
  }

  // Exportación controlada por plan (RFC-0025): la arma el SERVIDOR, que
  // valida el derecho, recorta al cupo mensual de filas y deja bitácora.
  const canExport = Boolean(user?.entitlements?.features.export_csv);
  const exportLimit = user?.entitlements?.limits.export_rows_per_month ?? null;
  const exportUsed = usage?.export_rows ?? 0;
  async function exportCsv() {
    if (exporting) return;
    setExporting(true);
    setNote(null);
    try {
      const { csv, rows } = await api.exportCsv(filters, EXPORT_ROWS);
      downloadCsv(`terrakairos-propiedades-${new Date().toISOString().slice(0, 10)}.csv`, csv);
      setNote(`✓ ${rows.toLocaleString('es-MX')} filas exportadas`);
      void refreshUser();
    } catch (e) {
      setNote(
        e instanceof ApiError && e.code === 'quota_exceeded' ? 'Cupo de exportación del mes agotado'
        : e instanceof ApiError && e.code === 'feature_not_in_plan' ? 'Tu plan no incluye exportar'
        : 'No se pudo exportar; intenta de nuevo',
      );
    } finally {
      setExporting(false);
      window.setTimeout(() => setNote(null), 5000);
    }
  }

  const listActive = listOpen && listMode === 'results';
  const watchActive = listOpen && listMode === 'watch';

  return (
    <div className="results-toolbar" role="toolbar" aria-label="Resultados">
      <button
        type="button"
        className={`tb-btn tb-primary ${listActive ? 'active' : ''}`}
        aria-pressed={listActive}
        onClick={() => onToggleList('results')}
        title={listActive ? 'Oculta la lista' : 'Muestra los resultados en lista'}
      >
        {listActive ? '✕ Ocultar lista' : '☰ Ver lista'}
      </button>
      {user && (
        <button
          type="button"
          className={`tb-btn ${watchActive ? 'active tb-watch' : ''}`}
          aria-pressed={watchActive}
          onClick={() => onToggleList('watch')}
          title="Las propiedades que tienes en observación"
        >
          {watchActive ? '★' : '☆'} Observados{watchItems.length ? ` (${watchItems.length})` : ''}
        </button>
      )}
      <button
        type="button"
        className="tb-btn"
        onClick={copyLink}
        title="Copia un enlace con los filtros actuales (y la propiedad abierta) para compartir esta búsqueda"
      >
        {copied ? '✓ Enlace copiado' : 'Copiar enlace'}
      </button>
      {/* Export cerrado sin sesión (RFC-0021 Bloque C §1.2) y, con sesión,
          sólo si el plan lo incluye (RFC-0025); si no, invita a mejorar. */}
      {user && canExport && (
        <button
          type="button"
          className="tb-btn"
          onClick={exportCsv}
          disabled={exporting}
          title={
            `Descarga hasta ${EXPORT_ROWS.toLocaleString('es-MX')} resultados filtrados a CSV.` +
            (exportLimit !== null ? ` Cupo del mes: ${exportUsed.toLocaleString('es-MX')} de ${exportLimit.toLocaleString('es-MX')} filas.` : '') +
            ' Queda registrado en la bitácora de tu organización.'
          }
        >
          {exporting ? 'Exportando…' : 'Exportar CSV'}
        </button>
      )}
      {user && !canExport && user.organization && (
        <button
          type="button"
          className="tb-btn tb-locked"
          onClick={openPlanPanel}
          title="Tu plan no incluye exportar a CSV. Ve las opciones para mejorarlo."
        >
          🔒 Exportar CSV
        </button>
      )}
      {note && <span className="results-note" role="status">{note}</span>}
    </div>
  );
}
